import { useState, type FormEvent } from 'react';
import styled from 'styled-components';
import { Button } from '@shared/ui/Button';
import { Card } from '@shared/ui/Card';
import { Table } from '@shared/ui/Table';
import { Modal } from '@shared/ui/Modal';
import { Field, Input, Label, Select } from '@shared/ui/Input';
import { Badge } from '@shared/ui/Badge';
import { Loader } from '@shared/ui/Progress';
import { ACCEPT_TEMPLATE } from '@shared/lib/fileAccept';
import { FileDropZone } from '@shared/ui/FileDropZone';
import { IconFile } from '@shared/ui/icons';
import {
  useListContractTemplatesQuery,
  useCreateContractTemplateMutation,
  useUploadContractTemplateMutation,
  useDeleteContractTemplateMutation,
} from '@shared/api';
import { CONTRACT_TYPE_OPTIONS, contractTypeLabel } from '@shared/lib/statusLabels';
import { downloadAuthorizedFile } from '@shared/lib/downloadFile';
import { useAppSelector } from '@app/hooks';
import { useAgentPermission } from '@entities/user';

const Bar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing(4)};
  gap: ${({ theme }) => theme.spacing(3)};
`;

const Note = styled.div`
  background: ${({ theme }) => theme.colors.primarySoft};
  border-left: 3px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.radius.sm};
  padding: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(4)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 1.55;
`;

const Empty = styled.td`
  text-align: center;
  padding: ${({ theme }) => theme.spacing(10)};
  color: ${({ theme }) => theme.colors.text.muted};
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 220px;
  padding: ${({ theme }) => theme.spacing(2)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text.primary};
  font: inherit;
  font-size: 13px;
  line-height: 1.45;
  font-family: ${({ theme }) => theme.font.mono};
  resize: vertical;
`;

const Tabs = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-bottom: ${({ theme }) => theme.spacing(3)};
`;

const TabBtn = styled.button<{ $active: boolean }>`
  padding: 8px 16px;
  border: 1px solid
    ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.border)};
  background: ${({ theme, $active }) => ($active ? theme.colors.primarySoft : 'transparent')};
  color: ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.text.primary)};
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 160ms ease-out;

  &:hover {
    background: ${({ theme }) => theme.colors.surface};
  }
`;

type CreateMode = 'text' | 'file';

export function ContractTemplates() {
  const { can_write } = useAgentPermission('statut');
  const { data, isLoading } = useListContractTemplatesQuery();
  const [createTemplate, { isLoading: isCreating }] = useCreateContractTemplateMutation();
  const [uploadTemplate, { isLoading: isUploading }] = useUploadContractTemplateMutation();
  const [deleteTemplate] = useDeleteContractTemplateMutation();

  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<CreateMode>('text');
  const [type, setType] = useState('works');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [content, setContent] = useState('');
  const [file, setFile] = useState<File | null>(null);

  function reset() {
    setName('');
    setDescription('');
    setContent('');
    setFile(null);
    setType('works');
    setMode('text');
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (mode === 'text') {
      if (!name.trim() || !content.trim()) return;
      await createTemplate({
        contract_type: type,
        name: name.trim(),
        content_text: content,
        description: description || undefined,
      }).unwrap();
    } else {
      if (!file || !name.trim()) return;
      const form = new FormData();
      form.append('file', file);
      form.append('contract_type', type);
      form.append('name', name.trim());
      if (description) form.append('description', description);
      await uploadTemplate(form).unwrap();
    }
    reset();
    setOpen(false);
  }

  async function onDelete(id: string, name: string) {
    if (!window.confirm(`Удалить типовую форму «${name}»? Это действие необратимо.`)) {
      return;
    }
    await deleteTemplate(id).unwrap();
  }

  const accessToken = useAppSelector((s) => s.auth.accessToken);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  // выгрузка проформы в DOCX. Бэкенд генерирует DOCX из
  // content_text на лету (оригинал в БД не хранится).
  async function onDownload(id: string, name: string) {
    setDownloadingId(id);
    try {
      const base = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';
      const res = await downloadAuthorizedFile(
        `${base}/agents/statut/contract-templates/${id}/download`,
        { accessToken, fallbackName: `template_${name}.docx` },
      );
      if (!res.ok) throw new Error(res.error ?? `HTTP ${res.status}`);
    } finally {
      setDownloadingId(null);
    }
  }

  if (isLoading) return <Loader />;
  const items = data?.items ?? [];

  return (
    <>
      <Note>
        Корпоративные стандарты договоров АО «САТКОМ». Шаблон хранится в БД, текст автоматически
        чанкуется и индексируется в RAG (с эмбеддингами), вместе с НПА в общем хранилище. При
        экспертизе договора LLM получает не весь текст шаблона, а **релевантные фрагменты** по
        семантическому поиску — это работает на любом размере шаблона.
        <br />
        На один тип одновременно одна активная форма; при добавлении новой старая автоматически
        деактивируется.
      </Note>

      <Bar>
        <div style={{ fontSize: 13, color: 'var(--osnova-text-muted, #888)' }}>
          Эталонные договоры САТКОМ, с которыми агент сверяет входящие договоры
          контрагентов и находит отклонения от стандарта.
          <br />
          Всего форм: <strong>{items.length}</strong> · активных:{' '}
          <strong>{items.filter((t) => t.is_active).length}</strong>
        </div>
        <Button
          type="button"
          onClick={() => setOpen(true)}
          disabled={!can_write}
          title={!can_write ? 'Только чтение' : undefined}
        >
          + Новая эталонная форма
        </Button>
      </Bar>

      <Card>
        <Table>
          <thead>
            <tr>
              <th>Тип</th>
              <th>Название</th>
              <th>Описание</th>
              <th>Активна</th>
              <th>RAG</th>
              <th>Добавлена</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <Empty colSpan={7}>
                  Эталонные формы не загружены. Нажмите «+ Новая эталонная форма», чтобы добавить
                  корпоративный стандарт для сверки договоров.
                </Empty>
              </tr>
            ) : (
              items.map((t) => (
                <tr key={t.id}>
                  <td>
                    <Badge>{contractTypeLabel(t.contract_type)}</Badge>
                  </td>
                  <td>{t.name}</td>
                  <td style={{ color: 'var(--osnova-text-muted, #888)' }}>
                    {t.description ?? '—'}
                  </td>
                  <td>
                    {t.is_active ? (
                      <Badge $tone="success">Активна</Badge>
                    ) : (
                      <Badge $tone="neutral">Архив</Badge>
                    )}
                  </td>
                  <td>
                    {t.knowledge_document_id ? (
                      <Badge $tone="success">✓ В RAG</Badge>
                    ) : (
                      <Badge $tone="neutral">Только текст</Badge>
                    )}
                  </td>
                  <td>{new Date(t.created_at).toLocaleDateString('ru-RU')}</td>
                  <td style={{ display: 'flex', gap: 6 }}>
                    <Button
                      $variant="secondary"
                      $size="sm"
                      onClick={() => onDownload(t.id, t.name)}
                      disabled={downloadingId === t.id}
                      title="Скачать в DOCX — для отправки контрагенту"
                    >
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                        }}
                      >
                        <IconFile size={14} />
                        {downloadingId === t.id ? 'Готовим…' : 'Скачать'}
                      </span>
                    </Button>
                    <Button
                      $variant="secondary"
                      $size="sm"
                      onClick={() => onDelete(t.id, t.name)}
                      disabled={!can_write}
                      title={!can_write ? 'Только чтение' : undefined}
                    >
                      Удалить
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </Card>

      <Modal
        open={open}
        title="Новая типовая форма"
        onClose={() => {
          reset();
          setOpen(false);
        }}
      >
        <form onSubmit={onSubmit}>
          <Tabs>
            <TabBtn type="button" $active={mode === 'text'} onClick={() => setMode('text')}>
              Ввести текст
            </TabBtn>
            <TabBtn type="button" $active={mode === 'file'} onClick={() => setMode('file')}>
              Загрузить DOCX / PDF
            </TabBtn>
          </Tabs>

          <Field>
            <Label htmlFor="tpl-type">Тип договора</Label>
            <Select id="tpl-type" value={type} onChange={(e) => setType(e.target.value)}>
              {CONTRACT_TYPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          </Field>

          <Field>
            <Label htmlFor="tpl-name">Название формы *</Label>
            <Input
              id="tpl-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="напр., «Типовой договор подряда САТКОМ 2026»"
              required
            />
          </Field>

          <Field>
            <Label htmlFor="tpl-desc">Краткое описание (опционально)</Label>
            <Input
              id="tpl-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="напр., «применяется для договоров на устройство монолита»"
            />
          </Field>

          {mode === 'text' ? (
            <Field>
              <Label htmlFor="tpl-content">Корпоративный стандарт (ключевые требования)</Label>
              <TextArea
                id="tpl-content"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder={
                  '5-10 пунктов с обязательными условиями АО «САТКОМ». Пример:\n\n' +
                  '1. Применимое право — РФ; подсудность — Арбитражный суд г. Москвы.\n' +
                  '2. Гарантийный срок — не менее 5 лет (ст. 756 ГК РФ).\n' +
                  '3. Неустойка САТКОМа — не более 0,1%/день, общий cap 10% от цены.\n' +
                  '4. Цена твёрдая, НДС 20% — включён.\n' +
                  '5. Сроки конкретные, в днях от события (аванс, передача площадки).'
                }
                required
              />
            </Field>
          ) : (
            <Field>
              <Label>Файл DOCX или PDF *</Label>
              {!file ? (
                <FileDropZone
                  accept={ACCEPT_TEMPLATE}
                  hint="DOCX или PDF с корпоративным шаблоном (текст будет извлечён)"
                  onFile={(f) => setFile(f)}
                />
              ) : (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: 12,
                    border: '1px solid var(--osnova-border, #2a2a2a)',
                    borderRadius: 8,
                  }}
                >
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    <IconFile size={16} />
                    {file.name}
                  </span>
                  <Button type="button" onClick={() => setFile(null)}>
                    Удалить
                  </Button>
                </div>
              )}
            </Field>
          )}

          <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
            <Button
              type="submit"
              disabled={isCreating || isUploading || !can_write}
              title={!can_write ? 'Только чтение' : undefined}
            >
              {isCreating || isUploading ? 'Сохраняем…' : 'Сохранить и сделать активной'}
            </Button>
            <Button
              type="button"
              $variant="secondary"
              onClick={() => {
                reset();
                setOpen(false);
              }}
            >
              Отмена
            </Button>
          </div>
        </form>
      </Modal>
    </>
  );
}
